import { useEffect, useMemo, useState } from 'react';
import { useUnit } from 'effector-react';
import clsx from 'clsx';
import { $accountListStore, $initialCurrenciesAvailableStore, $initialAccountsStore } from '@lib/store';
import { useTabs } from '@shared/hooks';
import { PaletteUtils } from '@shared/utils';
import { AccountStatus, AccountType } from '@shared/api/rest/accounts';
import { SortType } from '@shared/api/models';
import { List as Transactions } from '@widgets/transaction';
import { AccountsGrid, AccountsTable } from './widgets';
import { CardsTable } from '@widgets/card';
import { Select } from '@shared/widgets/select';
import { Button, Icon, PageContainer, Tabs, Text } from '@shared/widgets';
import GridViewIcon from '@assets/icons/grid-view.svg';
import LineViewIcon from '@assets/icons/line-view.svg';

const ORDER_OPTIONS = [
  { label: 'Ascending order', value: SortType.Asc },
  { label: 'Descending order', value: SortType.Desc },
];

export const Accounts = () => {
  const accountsStore = useUnit($accountListStore.store);
  const currenciesAvailableStore = useUnit($initialCurrenciesAvailableStore.store);
  const accountDefaultListStore = useUnit($initialAccountsStore.store);

  const [isGridView, setIsGridView] = useState<boolean>(true);
  const [order, setOrder] = useState<SortType>(SortType.Desc);
  const [currency, setCurrency] = useState<string | undefined>();

  const [status, setStatus] = useState<AccountStatus | undefined>(AccountStatus.ACTIVE);

  const tabsState = useTabs();

  useEffect(() => {
    if (accountDefaultListStore.data.length > 0) {
      const hasActiveAccounts = accountDefaultListStore.data.some((account) => account.status === AccountStatus.ACTIVE);
      if (status === AccountStatus.ACTIVE && !hasActiveAccounts) {
        setStatus(undefined);
      }
    }
  }, [accountDefaultListStore.data, status]);

  useEffect(() => {
    $accountListStore.events.update({
      Status: status,
      SortType: order,
      Currency: currency,
    });
  }, [status, order, currency]);

  const statusOptions = useMemo(() => [...new Set(accountDefaultListStore.data.map((account) => account.status))], [accountDefaultListStore.data]);
  const accountIds = useMemo(
    () => (accountDefaultListStore.data || []).filter((item) => item.type === tabsState.tab).map((item) => item.id),
    [tabsState.tab, accountDefaultListStore],
  );
  const cardAccountsIds = useMemo(
    () => accountsStore.data.filter((account) => account.type === AccountType.Card).map((account) => account.id),
    [accountsStore.data],
  );

  return (
    <PageContainer
      title='Accounts'
      header={
        <div className='flex'>
          <Text>Accounts</Text>
          {!accountsStore.loading && (
            <span
              className={clsx(
                'rounded-2xl px-2 ml-2 h-8 w-8 flex justify-center dark:bg-primary text-white',
                PaletteUtils.isInsigniaPalette && 'bg-soft-black',
                PaletteUtils.isVcfcPalette && 'bg-primary',
              )}
            >
              <span className='opacity-1 transition ease-in'>{accountsStore.data.length || 0}</span>
            </span>
          )}
        </div>
      }
      topRightAddons={
        <>
          <Button color='icon' onClick={() => setIsGridView(true)}>
            <Icon icon={<GridViewIcon />} className={clsx(isGridView && '!text-primary')} />
          </Button>
          <Button color='icon' className='ml-4' onClick={() => setIsGridView(false)}>
            <Icon icon={<LineViewIcon />} className={clsx(!isGridView && '!text-primary')} />
          </Button>
        </>
      }
    >
      <div className='flex flex-row max-[900px]:flex-col max-[900px]:gap-2'>
        {tabsState.hasCard && tabsState.hasPayment && <Tabs options={tabsState.tabs} value={tabsState.tab!} onChange={tabsState.onChangeTab} />}
        <div className='flex flex-row ml-auto max-[900px]:ml-0 space-x-4'>
          <Select
            className='min-w-[140px]'
            items={statusOptions}
            nullValue={{ isEnable: true, text: 'All Statuses' }}
            value={status}
            onChange={setStatus}
          />
          <Select className='min-w-[190px]' items={ORDER_OPTIONS} value={order} onChange={(val) => setOrder(val!)} />
          <Select
            className='min-w-[160px]'
            items={currenciesAvailableStore.data}
            nullValue={{ isEnable: true, text: 'All currencies' }}
            value={currency}
            onChange={(val) => setCurrency(val!)}
          />
        </div>
      </div>
      <div className='my-12'>
        {isGridView && (
          <AccountsGrid accountType={tabsState.tab!} isLoading={accountsStore.loading} accounts={accountsStore.data} currency={currency} />
        )}
        {!isGridView && <AccountsTable accountType={tabsState.tab!} isLoading={accountsStore.loading} accounts={accountsStore.data} />}
      </div>
      {tabsState.tab == AccountType.Card && (
        <div className='flex flex-col gap-6'>
          <Text weight='semibold' size='2xl'>
            Related Cards
          </Text>
          <CardsTable listType={!isGridView} accountIds={cardAccountsIds} currency={currency} />
        </div>
      )}
      <div className='flex flex-col w-full gap-6'>
        <Text weight='semibold' size='2xl'>
          Transactions
        </Text>
        <Transactions accountType={tabsState.tab!} accountIds={accountIds} search filter />
      </div>
    </PageContainer>
  );
};
